'use client';

import H2 from '@/components/typography/h2';
import H3 from '@/components/typography/h3';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { motion, useScroll, useTransform } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Dot } from 'lucide-react';
import { useEffect, useRef } from 'react';

export default function AboutStickyCardScroll() {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end end'],
    });

    const coffeeRef = useRef<HTMLDivElement | null>(null);
    gsap.registerPlugin(ScrollTrigger);

    useEffect(() => {
        if (!coffeeRef.current) return;

        gsap.fromTo(
            coffeeRef.current,
            { y: 50 },
            {
                y: -50,
                ease: 'none',
                scrollTrigger: {
                    trigger: coffeeRef.current,
                    start: 'top center+=100',
                    end: '+=250', // ← finishes after 250px of scroll
                    scrub: true,
                },
            },
        );
    }, []);

    const sections = [
        {
            title: 'Strategie',
            content: (
                <P>
                    Het start bij ons met een behapbare strategie. Geen dik
                    rapport voor in de la, maar een helder recept. Zo brengen we
                    ingewikkelde uitdagingen terug tot een eenvoudig en
                    emotioneel vertrekpunt. Zonder de complexiteit van de echte
                    wereld geweld aan te doen.
                </P>
            ),
            list: [
                'Doelgroepanalyse',
                'communicatiestrategie',
                'merkstrategie',
                'mediastrategie',
            ],
        },
        {
            title: 'Creatie',
            content: (
                <P>
                    Wij bedenken ideeën die mensen lekker maken voor
                    verandering. Smakelijke campagnes, social content,
                    activaties, producten en design. Soms in de vorm van een
                    trui, en ook wel eens een blik soep.
                </P>
            ),
            list: [
                'Creatieve campagnes',
                'Design',
                'Activatie',
                'content & social',
            ],
        },
        {
            title: 'PR',
            content: (
                <P>
                    Met het grootste en meest diverse foodnetwerk van Nederland,
                    zorgen we dat goede verhalen het juiste podium krijgen. Van
                    mainstream nieuwsplatforms tot hyper-specialistische
                    vakmedia en lifestyle magazines.
                </P>
            ),
            list: [
                'Persberichten en pitches',
                'Persevents & pakketten',
                'Doorlopende PR',
                'Influencers & creators',
                'Branded content',
            ],
        },
    ];
    const total = sections.length;

    if (typeof window === 'undefined')
        return <div className="bg-beige h-screen w-full" />;

    return (
        <div
            className="relative flex"
            style={{ minHeight: `${sections.length * 90}svh` }}
            ref={ref}
        >
            {/* Left column */}
            <div className="border-primary relative w-[42.25%] border-r border-b pt-[16rem]">
                <div className="sticky top-[15rem] mt-8 mb-[350px]">
                    <div className="flex justify-center">
                        <div className="flex w-[424px] flex-col gap-5">
                            <H2>Wat maken we</H2>
                            <H3>
                                Bij Food Cabinet maken we strategie, creatie en
                                PR. Los, of - nog effectiever - in een
                                geïntegreerde campagne.
                            </H3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right column */}
            <div className="relative w-[57.75%]">
                <div className="sticky top-0 h-screen">
                    {sections.slice(0, 1).map((section, i) => (
                        <div
                            key={i}
                            className={`border-primary bg-beige absolute left-0 flex h-screen w-full items-center justify-center border-t-0 pb-30`}
                        >
                            <AboutUsSection {...section} />
                        </div>
                    ))}
                    {sections.slice(1).map((section, i) => (
                        <MotionAboutUsSection
                            key={i}
                            total={total}
                            scrollYProgress={scrollYProgress}
                            i={i}
                            section={section}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

type Section = {
    title?: string;
    content?: React.ReactNode;
    list?: string[];
};

function MotionAboutUsSection({
    total,
    scrollYProgress,
    i,
    section,
}: {
    total: number;
    scrollYProgress: ReturnType<typeof useScroll>['scrollYProgress'];
    i: number;
    section: Section;
}) {
    const index = i + 1;
    const step = 1 / (total - 1);
    const start = (index - 1) * step;
    const end = index * step;
    const y = useTransform(scrollYProgress, [start, end], ['100%', '0%']);

    return (
        <motion.div
            key={index}
            className={`border-primary bg-beige absolute left-0 flex h-screen w-full items-center justify-center pb-30 ${
                index === 2 || index === 3 ? 'border-t border-b' : 'border-t'
            }`}
            style={{ y }}
        >
            <AboutUsSection {...section} />
        </motion.div>
    );
}

function AboutUsSection({
    title,
    content,
    list,
}: {
    title?: string;
    content?: React.ReactNode;
    list?: string[];
}) {
    return (
        <div className="flex max-w-[769px] flex-col px-5">
            {title && <H3>{title}</H3>}
            <div className="mt-5 mb-[40px]">{content}</div>
            {list && (
                <ul className="flex flex-wrap gap-4">
                    {list.map((item, itemIndex) => (
                        <li key={itemIndex}>
                            <div className="flex items-center gap-2">
                                <Dot className="bg-primary h-2.5 w-2.5 rounded-full lg:h-3.5 lg:w-3.5" />
                                <Label>{item}</Label>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
